import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import CreateCandidateModal from "bundles/Internal/components/common/CreateCandidateModal";

// Mounted once for the whole internal app. Rendered per-page, a route change unmounted it and took
// the typed values, the picked File and the running CV parse with it.
const CandidateModalContext = createContext(null);

export const useCandidateModal = () => {
  const context = useContext(CandidateModalContext);
  if (!context) throw new Error("useCandidateModal must be used inside CandidateModalProvider");
  return context;
};

const CandidateModalProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const close = useCallback(() => {
    setConfig(null);
    setSubmitting(false);
  }, []);

  const value = useMemo(() => ({
    openCreateCandidate: (options = {}) => setConfig({ ...options, isEditMode: false }),
    openEditCandidate: (options = {}) => setConfig({ ...options, isEditMode: true }),
    // For a detail that only arrives after opening (the external-profile CV download).
    updateCandidateModal: (patch) => setConfig((prev) => (prev ? { ...prev, ...patch } : prev)),
    closeCandidateModal: close,
  }), [close]);

  // `close` goes to the caller so it keeps deciding when a save counts as done. A handler that
  // returns its promise gets the spinner; one that returns nothing still works.
  const handleSubmit = useCallback((values, helpers) => {
    const result = config?.onSubmit?.(values, { ...helpers, close });
    if (!result?.finally) return;

    setSubmitting(true);
    result.finally(() => setSubmitting(false));
  }, [config, close]);

  return (
    <CandidateModalContext.Provider value={value}>
      {children}
      <CreateCandidateModal
        open={Boolean(config)}
        onClose={close}
        onSubmit={handleSubmit}
        submitting={submitting}
        isEditMode={config?.isEditMode || false}
        candidateId={config?.candidateId || null}
        defaultValues={config?.defaultValues}
        defaultResumeFile={config?.defaultResumeFile}
        existingResumeUrl={config?.existingResumeUrl || null}
        cvVaultItemId={config?.cvVaultItemId || null}
        cvFilename={config?.cvFilename || null}
        parsedPayload={config?.parsedPayload || null}
        initialCvText={config?.cvText || null}
      />
    </CandidateModalContext.Provider>
  );
};

export default CandidateModalProvider;
